import { FC, useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  Users, 
  Clock, 
  Calendar, 
  Edit, 
  Trash2, 
  Eye,
  MapPin,
  AlertCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CapacityIndicator } from './CapacityIndicator';

interface SlotHoverCardProps {
  event: any;
  isVisible: boolean;
  position: { x: number; y: number };
  onEdit?: (slotId: string, slotData: any) => void;
  onDelete?: (slotId: string, slotData: any) => void;
  onViewCustomAdventure?: (bookingId: string, bookingData: any) => void;
  onClose: () => void;
}

export const SlotHoverCard: FC<SlotHoverCardProps> = ({
  event,
  isVisible,
  position,
  onEdit,
  onDelete,
  onViewCustomAdventure,
  onClose
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Check if this is a custom adventure booking
  const isCustomAdventure = event.extendedProps?.type === 'custom-adventure' || 
                           event.extendedProps?.isCustomAdventure;

  // Get appropriate ID based on event type
  const eventId = isCustomAdventure 
    ? event.extendedProps?.bookingId 
    : (event.id || event.extendedProps?.slotId || event.extendedProps?.id);

  // Adjust position to keep card in viewport
  useEffect(() => {
    if (isVisible && cardRef.current) {
      const card = cardRef.current;
      const cardRect = card.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let newX = position.x;
      let newY = position.y;
      
      // Adjust horizontal position
      if (position.x + cardRect.width > viewportWidth) {
        newX = position.x - cardRect.width - 10;
      }
      
      // Adjust vertical position
      if (position.y + cardRect.height > viewportHeight) {
        newY = position.y - cardRect.height - 10;
      }
      
      setAdjustedPosition({ x: newX, y: newY });
    }
  }, [isVisible, position]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose]);

  if (!isVisible || !event) return null;

  const handleEdit = () => {
    if (isCustomAdventure && onViewCustomAdventure) {
      onViewCustomAdventure(eventId, event.extendedProps);
    } else if (onEdit) {
      onEdit(eventId, {
        id: eventId,
        title: event.title,
        start: event.start,
        end: event.end,
        ...event.extendedProps
      });
    }
    onClose();
  };

  const handleDelete = () => {
    if (!isCustomAdventure && onDelete) {
      onDelete(eventId, {
        id: eventId,
        title: event.title,
        start: event.start,
        end: event.end,
        ...event.extendedProps
      });
    }
    onClose();
  };

  // Render custom adventure card
  if (isCustomAdventure) {
    const participants = event.extendedProps?.participants;
    const totalParticipants = participants ? 
      (participants.adults || 0) + (participants.children || 0) + (participants.students || 0) : 0;
    
    return (
      <div
        ref={cardRef}
        className="fixed z-50 w-80 bg-white rounded-xl border border-slate-200/60 shadow-xl shadow-slate-900/10 backdrop-blur-sm"
        style={{
          left: `${adjustedPosition.x}px`,
          top: `${adjustedPosition.y}px`,
          animation: 'fadeIn 0.2s ease-out'
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-100/80 bg-gradient-to-r from-emerald-50 to-emerald-100/50 rounded-t-xl">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4 text-emerald-600" />
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200/50 text-xs">
                  Custom Adventure
                </Badge>
              </div>
              <h3 className="font-semibold text-slate-900 text-sm leading-tight">
                {event.title}
              </h3>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Date & Time */}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span>{format(new Date(event.start), 'MMM dd, yyyy')}</span>
            <Clock className="w-4 h-4 text-slate-400 ml-2" />
            <span>
              {format(new Date(event.start), 'HH:mm')} - 
              {event.end ? format(new Date(event.end), 'HH:mm') : 'TBD'}
            </span>
          </div>

          {/* Customer Info */}
          {event.extendedProps?.customerName && (
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-slate-400" />
              <span className="text-slate-700 font-medium">
                {event.extendedProps.customerName}
              </span>
              {totalParticipants > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {totalParticipants} guests
                </Badge>
              )}
            </div>
          )}


          {/* Request Number */}
          {event.extendedProps?.requestNumber && (
            <div className="text-xs text-slate-500">
              Request #{event.extendedProps.requestNumber}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-3 border-t border-slate-100/80 bg-slate-50/50 rounded-b-xl">
          <Button
            onClick={handleEdit}
            size="sm"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Eye className="w-3 h-3 mr-2" />
            View Booking Details
          </Button>
        </div>
      </div>
    );
  }

  // Render regular activity slot card
  const capacity = event.extendedProps?.capacity || 0;
  const booked = event.extendedProps?.booked || 0;
  const available = capacity - booked;
  const price = event.extendedProps?.price || 0;
  const bookingPercentage = capacity > 0 ? (booked / capacity) * 100 : 0;

  return (
    <div
      ref={cardRef}
      className="fixed z-50 w-80 bg-white rounded-xl border border-slate-200/60 shadow-xl shadow-slate-900/10 backdrop-blur-sm"
      style={{
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`,
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      {/* Header */}
      <div className="p-4 border-b border-slate-100/80">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 text-sm leading-tight mb-2">
              {event.title}
            </h3>
            <CapacityIndicator 
              booked={booked} 
              capacity={capacity} 
              size="md" 
            />
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge 
              variant="secondary" 
              className={`text-xs ${
                bookingPercentage >= 90 ? 'bg-red-100 text-red-800 border-red-200/50' :
                bookingPercentage >= 70 ? 'bg-amber-100 text-amber-800 border-amber-200/50' :
                bookingPercentage >= 30 ? 'bg-blue-100 text-blue-800 border-blue-200/50' :
                'bg-emerald-100 text-emerald-800 border-emerald-200/50'
              }`}
            >
              {Math.round(bookingPercentage)}% Booked
            </Badge>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Date & Time */}
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Calendar className="w-4 h-4 text-slate-400" />
          <span>{format(new Date(event.start), 'MMM dd, yyyy')}</span>
          <Clock className="w-4 h-4 text-slate-400 ml-2" />
          <span>
            {format(new Date(event.start), 'HH:mm')} - 
            {event.end ? format(new Date(event.end), 'HH:mm') : 'TBD'}
          </span>
        </div>

        {/* Capacity Details */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-lg font-bold text-slate-900">{available}</div>
            <div className="text-xs text-slate-600">Available</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-2">
            <div className="text-lg font-bold text-blue-600">{booked}</div>
            <div className="text-xs text-slate-600">Booked</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-2">
            <div className="text-lg font-bold text-emerald-600">{capacity}</div>
            <div className="text-xs text-slate-600">Total</div>
          </div>
        </div>


        {/* Status Alerts */}
        {bookingPercentage >= 90 && (
          <div className="flex items-center gap-2 text-sm bg-red-50 rounded-lg p-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <span className="text-red-700">Nearly full - only {available} spots left</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-3 border-t border-slate-100/80 bg-slate-50/50 rounded-b-xl">
        <div className="flex gap-2">
          <Button
            onClick={handleEdit}
            size="sm"
            variant="outline"
            className="flex-1"
          >
            <Edit className="w-3 h-3 mr-2" />
            Edit
          </Button>
          <Button
            onClick={handleDelete}
            size="sm"
            variant="outline"
            className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3 mr-2" />
            Delete
          </Button>
        </div>
      </div>

    </div>
  );
};
